/**
 * FEAT-041 / S-255 — Platform-operator cross-tenant audit log console.
 *
 * Filterable (tenant, action, resource type, date range), cursor-paginated
 * table over GET /admin/audit-log. Client-side guard mirrors the server
 * 403 (usePlatformAdmin → redirect); the real enforcement is the endpoint.
 */

import { useState } from "react";
import { Navigate } from "react-router-dom";
import { useApiQuery } from "@/lib/api";
import { usePlatformAdmin } from "../components/AuthProvider";

interface AuditRow {
  id: string;
  occurred_at: string;
  actor_user_id: string;
  tenant_id: number;
  tenant_name: string;
  agencyclient_id: number | null;
  action: string;
  resource_type: string;
  resource_id: string;
  payload_json: unknown;
}

interface AuditLogResponse {
  data: AuditRow[];
  next_cursor: string | null;
}

interface Filters {
  tenantId: string;
  action: string;
  resourceType: string;
  from: string;
  to: string;
}

const EMPTY_FILTERS: Filters = { tenantId: "", action: "", resourceType: "", from: "", to: "" };

function buildQuery(filters: Filters, cursor: string | null): string {
  const params = new URLSearchParams();
  if (filters.tenantId) params.set("tenant_id", filters.tenantId);
  if (filters.action) params.set("action", filters.action);
  if (filters.resourceType) params.set("resource_type", filters.resourceType);
  if (filters.from) params.set("from", new Date(filters.from).toISOString());
  if (filters.to) params.set("to", new Date(filters.to).toISOString());
  if (cursor) params.set("cursor", cursor);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

export function AdminAuditView() {
  const isPlatformAdmin = usePlatformAdmin();
  const [pending, setPending] = useState<Filters>(EMPTY_FILTERS);
  const [applied, setApplied] = useState<Filters>(EMPTY_FILTERS);
  const [cursor, setCursor] = useState<string | null>(null);
  const [cursorHistory, setCursorHistory] = useState<(string | null)[]>([]);

  const query = buildQuery(applied, cursor);
  const { data, isLoading, error } = useApiQuery<AuditLogResponse>(
    ["admin-audit-log", applied, cursor],
    `/admin/audit-log${query}`,
    { enabled: isPlatformAdmin }
  );

  if (!isPlatformAdmin) return <Navigate to="/kanban" replace />;

  function applyFilters() {
    setApplied(pending);
    setCursor(null);
    setCursorHistory([]);
  }

  function clearFilters() {
    setPending(EMPTY_FILTERS);
    setApplied(EMPTY_FILTERS);
    setCursor(null);
    setCursorHistory([]);
  }

  function goNext() {
    if (!data?.next_cursor) return;
    setCursorHistory((h) => [...h, cursor]);
    setCursor(data.next_cursor);
  }

  function goPrev() {
    setCursorHistory((h) => {
      if (h.length === 0) return h;
      const prev = h[h.length - 1];
      setCursor(prev);
      return h.slice(0, -1);
    });
  }

  return (
    <main className="main settings-main">
      <div className="settings-page">
        <header className="settings-header">
          <div>
            <h1 className="settings-title">Audit Log</h1>
            <p className="settings-sub">
              Cross-tenant privileged-action trail across the platform.
            </p>
          </div>
        </header>

        <div
          style={{
            display: "flex",
            gap: 8,
            flexWrap: "wrap",
            alignItems: "flex-end",
            marginBottom: 12,
          }}
        >
          <label className="settings-field" style={{ minWidth: 90 }}>
            <span className="settings-label">Tenant ID</span>
            <input
              className="settings-input"
              type="text"
              inputMode="numeric"
              value={pending.tenantId}
              onChange={(e) => setPending((f) => ({ ...f, tenantId: e.target.value }))}
            />
          </label>
          <label className="settings-field" style={{ minWidth: 160 }}>
            <span className="settings-label">Action</span>
            <input
              className="settings-input"
              type="text"
              placeholder="e.g. piece.approve"
              value={pending.action}
              onChange={(e) => setPending((f) => ({ ...f, action: e.target.value }))}
            />
          </label>
          <label className="settings-field" style={{ minWidth: 130 }}>
            <span className="settings-label">Resource type</span>
            <input
              className="settings-input"
              type="text"
              placeholder="e.g. piece"
              value={pending.resourceType}
              onChange={(e) => setPending((f) => ({ ...f, resourceType: e.target.value }))}
            />
          </label>
          <label className="settings-field">
            <span className="settings-label">From</span>
            <input
              className="settings-input"
              type="date"
              value={pending.from}
              onChange={(e) => setPending((f) => ({ ...f, from: e.target.value }))}
            />
          </label>
          <label className="settings-field">
            <span className="settings-label">To</span>
            <input
              className="settings-input"
              type="date"
              value={pending.to}
              onChange={(e) => setPending((f) => ({ ...f, to: e.target.value }))}
            />
          </label>
          <button type="button" className="btn-ghost" onClick={applyFilters}>
            Apply
          </button>
          <button type="button" className="btn-ghost" onClick={clearFilters}>
            Clear
          </button>
        </div>

        {isLoading && <div className="panel-empty">Loading…</div>}
        {error && <div className="panel-empty">Could not load audit log.</div>}

        {data && !isLoading && (
          <>
            <table className="workspaces-table">
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Tenant</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Resource</th>
                  <th>Payload</th>
                </tr>
              </thead>
              <tbody>
                {data.data.map((r) => (
                  <tr key={r.id}>
                    <td className="mono">{new Date(r.occurred_at).toLocaleString()}</td>
                    <td>{r.tenant_name}</td>
                    <td className="mono">{r.actor_user_id}</td>
                    <td>{r.action}</td>
                    <td className="mono">
                      {r.resource_type}#{r.resource_id}
                    </td>
                    <td className="mono" style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis" }}>
                      {r.payload_json ? JSON.stringify(r.payload_json) : ""}
                    </td>
                  </tr>
                ))}
                {data.data.length === 0 && (
                  <tr>
                    <td colSpan={6} className="panel-empty">
                      No audit entries for this filter.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>

            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <button
                type="button"
                className="btn-ghost"
                disabled={cursorHistory.length === 0}
                onClick={goPrev}
              >
                ← Prev
              </button>
              <button
                type="button"
                className="btn-ghost"
                disabled={!data.next_cursor}
                onClick={goNext}
              >
                Next →
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
